import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation, Link } from "react-router-dom";
import { staffMe, staffLogout, getStaffToken, setStaffToken, getStaffNotificaciones } from "@/lib/staffApi";
import { LogOut, Bell, LayoutDashboard, Inbox, Shield, Loader2 } from "lucide-react";
import StaffLogin from "@/pages/staff/StaffLogin";
import StaffChangePassword from "@/pages/staff/StaffChangePassword";
import AsesorBandeja from "@/pages/staff/AsesorBandeja";
import AsesorExpediente from "@/pages/staff/AsesorExpediente";
import StaffDashboard from "@/pages/staff/StaffDashboard";
import AdminPanel from "@/pages/staff/AdminPanel";

const StaffCtx = createContext(null);
export const useStaff = () => useContext(StaffCtx);

const ROLE_LABEL = {
  asesor: "Asesor", mantenimiento: "Mantenimiento", administrador: "Administrador", ceo: "CEO",
};

function StaffLayout({ children }) {
  const { user, logout } = useStaff();
  const [notifs, setNotifs] = useState([]);
  const [openBell, setOpenBell] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    getStaffNotificaciones().then(setNotifs).catch(() => {});
  }, [loc.pathname]);

  const nav = [
    { to: "/staff/bandeja", label: "Bandeja", icon: Inbox, roles: ["asesor", "administrador", "ceo"] },
    { to: "/staff/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["asesor", "administrador", "ceo"] },
    { to: "/staff/admin", label: "Administración", icon: Shield, roles: ["administrador", "ceo", "mantenimiento"] },
  ].filter((n) => n.roles.includes(user.role));

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-neutral-100 flex" data-testid="staff-layout">
      <aside className="w-60 border-r border-neutral-800 bg-[#0d0d0d] flex flex-col">
        <div className="px-5 py-6 border-b border-neutral-800">
          <div className="text-orange-500 font-black tracking-tight text-lg">AUTOFINANZA</div>
          <div className="text-xs text-neutral-500 mt-1">Panel interno</div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {nav.map((n) => {
            const active = loc.pathname.startsWith(n.to);
            const Icon = n.icon;
            return (
              <Link key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase()}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  active ? "bg-orange-500/15 text-orange-400" : "text-neutral-400 hover:bg-neutral-900 hover:text-neutral-200"
                }`}>
                <Icon size={18} /> {n.label}
              </Link>
            );
          })}
        </nav>
        <div className="px-4 py-4 border-t border-neutral-800">
          <div className="text-sm font-medium">{user.name}</div>
          <div className="text-xs text-neutral-500 mb-3">{ROLE_LABEL[user.role]}</div>
          <button onClick={logout} data-testid="staff-logout-btn"
            className="w-full flex items-center justify-center gap-2 text-sm py-2 rounded-lg bg-neutral-900 hover:bg-neutral-800 text-neutral-300">
            <LogOut size={16} /> Cerrar sesión
          </button>
        </div>
      </aside>
      <div className="flex-1 flex flex-col">
        <header className="h-14 border-b border-neutral-800 flex items-center justify-end px-6 gap-4 bg-[#0d0d0d]">
          <div className="relative">
            <button onClick={() => setOpenBell((v) => !v)} data-testid="staff-bell-btn"
              className="relative p-2 rounded-lg hover:bg-neutral-900">
              <Bell size={20} className="text-neutral-300" />
              {notifs.filter((n) => !n.leida).length > 0 && (
                <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-orange-500" />
              )}
            </button>
            {openBell && (
              <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-auto bg-[#131313] border border-neutral-800 rounded-xl shadow-2xl z-50 p-2"
                data-testid="staff-bell-dropdown">
                {notifs.length === 0 && <div className="text-sm text-neutral-500 p-3">Sin notificaciones</div>}
                {notifs.map((n) => (
                  <div key={n.id} className="p-3 rounded-lg hover:bg-neutral-900 text-sm">
                    <div className="text-orange-400 text-xs font-semibold">{n.evento}</div>
                    <div className="text-neutral-300">{n.mensaje}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </header>
        <main className="flex-1 overflow-auto p-6">{children}</main>
      </div>
    </div>
  );
}

function Protected({ children }) {
  const { user, loading } = useStaff();
  if (loading)
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center text-neutral-400">
        <Loader2 className="animate-spin" />
      </div>
    );
  if (!user) return <Navigate to="/staff/login" replace />;
  if (user.must_change_password) return <Navigate to="/staff/cambiar-password" replace />;
  return <StaffLayout>{children}</StaffLayout>;
}

export default function StaffApp() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    if (!getStaffToken()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const u = await staffMe();
      setUser(u);
    } catch {
      setStaffToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const logout = async () => {
    try {
      await staffLogout();
    } catch {}
    setStaffToken(null);
    setUser(null);
    navigate("/staff/login");
  };

  return (
    <StaffCtx.Provider value={{ user, setUser, loading, refresh, logout }}>
      <Routes>
        <Route index element={<Navigate to="/staff/login" replace />} />
        <Route path="login" element={<StaffLogin />} />
        <Route path="cambiar-password" element={<StaffChangePassword />} />
        <Route path="bandeja" element={<Protected><AsesorBandeja /></Protected>} />
        <Route path="expediente/:id" element={<Protected><AsesorExpediente /></Protected>} />
        <Route path="dashboard" element={<Protected><StaffDashboard /></Protected>} />
        <Route path="admin" element={<Protected><AdminPanel /></Protected>} />
        <Route path="*" element={<Navigate to="/staff/login" replace />} />
      </Routes>
    </StaffCtx.Provider>
  );
}
